import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate } from 'react-router'
import filterStore from 'store/filter/filter'
import {
  compressFilters,
  FILTER_QUERYSTRING_PARAM,
  getCompressedFilterParams,
  getDecompressedFilterParams,
  getFilterString,
  normalizeFilterBlob,
  parseExistingFilters,
  parseFilterFromUrlString,
} from 'store/filter/urlFilter.helper'
import sentry from 'utils/sentry/sentry'
import { useSnapshot } from 'valtio'

type UseActiveTagsProps = {
  availableFiltersSubset: string[][] // The list of filters to show in the dropdowns. This should be a subset of 'filterStore.allTags'
}

type UseActiveTagsReturn = {
  activeTags: Set<string>
  urlFilters: Readonly<Record<string, readonly string[]>> | undefined
  checkedFiltersPerDropdown: Record<string, Record<string, boolean>>
  isActiveTagsFiltered: boolean
}

export function getTagsByTopLevelTag(routeTags: string[]): Record<string, string[]> {
  const byTopLevelTag: Record<string, string[]> = {}

  for (const tag of routeTags) {
    const [topLevelTag] = tag.split('/')
    if (!byTopLevelTag[topLevelTag]) byTopLevelTag[topLevelTag] = []
    byTopLevelTag[topLevelTag].push(tag)
  }
  return byTopLevelTag
}

function getCheckedFiltersPerDropdown(
  tagsByTopLevelTag: Record<string, string[]>,
  urlFilters: Readonly<Record<string, readonly string[]>>
): Record<string, Record<string, boolean>> {
  const out: Record<string, Record<string, boolean>> = {}
  Object.entries(tagsByTopLevelTag).forEach(([topLevelTag, topLevelTags]) => {
    out[topLevelTag] = {}
    const urlForTopLevelTag = urlFilters[topLevelTag] ?? []
    topLevelTags.forEach((tag) => {
      out[topLevelTag][tag] = urlForTopLevelTag.includes(tag.split('/')[1])
    })
  })
  return out
}

export function getActiveTags(
  tagsByTopLevelTag: Record<string, string[]>,
  urlFilters: Readonly<Record<string, readonly string[]>>
): Set<string> {
  const active: string[] = []
  Object.entries(tagsByTopLevelTag).forEach(([topLevelTag, topLevelTags]) => {
    const urlFiltersForTopLevelTag = urlFilters[topLevelTag]
    if (
      !urlFiltersForTopLevelTag ||
      urlFiltersForTopLevelTag.length === 0 ||
      urlFiltersForTopLevelTag.length === topLevelTags.length
    ) {
      active.push(...topLevelTags)
    } else {
      active.push(...topLevelTags.filter((tag) => urlFiltersForTopLevelTag.includes(tag.split('/')[1])))
    }
  })
  return new Set(active)
}

/**
 * Decompressing the filter param is async, so a freshly mounted consumer would otherwise
 * start from an empty filter set and only learn the truth a round-trip later. During that
 * window an applied filter is indistinguishable from no filter at all, which makes callers
 * such as the "Save current filter" control render as if nothing were filtered.
 *
 * Every parse is therefore shared here, keyed by the compressed param it came from, so any
 * later consumer of the same URL resolves synchronously on its first render.
 */
const parsedFiltersByCompressedParam = new Map<string, Record<string, string[]>>()

// Only the URLs a session actually revisits are worth keeping; bound the cache so a long
// session visiting many distinct filter URLs cannot grow it indefinitely. Map preserves
// insertion order, so re-inserting on access makes the first key the least recently used.
const MAX_CACHED_PARSED_FILTERS = 25

/**
 * The already-known filters for a compressed param, or undefined if it still has to be
 * decompressed. An absent param is known to mean "no filters" without any parsing.
 */
function getKnownUrlFilters(compressedFilters: string | null): Record<string, string[]> | undefined {
  if (!compressedFilters) return {}

  // Keyed on the canonical blob, not the raw param: the same filter reaches us in
  // either base64 alphabet (a legacy bookmark vs a freshly written URL), and two
  // entries for one filter would defeat the seeding this cache exists for.
  const key = normalizeFilterBlob(compressedFilters)
  const parsed = parsedFiltersByCompressedParam.get(key)
  if (parsed) {
    parsedFiltersByCompressedParam.delete(key)
    parsedFiltersByCompressedParam.set(key, parsed)
  }
  return parsed
}

function cacheParsedUrlFilters(compressedFilters: string, parsed: Record<string, string[]>): void {
  const key = normalizeFilterBlob(compressedFilters)
  parsedFiltersByCompressedParam.delete(key)
  parsedFiltersByCompressedParam.set(key, parsed)

  while (parsedFiltersByCompressedParam.size > MAX_CACHED_PARSED_FILTERS) {
    const leastRecentlyUsed = parsedFiltersByCompressedParam.keys().next().value
    if (leastRecentlyUsed === undefined) break
    parsedFiltersByCompressedParam.delete(leastRecentlyUsed)
  }
}

function publishUrlFilters(compressedFilters: string | null, filters: Record<string, string[]>): void {
  filterStore.urlFilters = filters
  filterStore.resolvedFilterBlob = compressedFilters ? normalizeFilterBlob(compressedFilters) : null
}

// Sentinel for "we have not resolved any filter param yet", distinct from null (= no param).
const UNRESOLVED_FILTER_PARAM = ''

export function useActiveTags({ availableFiltersSubset }: UseActiveTagsProps): UseActiveTagsReturn {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <we stringify the dependency to avoid re-renders when only the reference has changed>
  const routeTags = useMemo(
    () => [...new Set(availableFiltersSubset.flatMap((filter) => filter))],
    [JSON.stringify(availableFiltersSubset)]
  )

  const { urlFilters: parsedUrlFilters, resolvedFilterBlob } = useSnapshot(filterStore, { sync: true })
  const [compressedFilters, setCompressedFilters] = useState<string | null>(UNRESOLVED_FILTER_PARAM)
  const location = useLocation()
  const currentFilterParam = getCompressedFilterParams()
  const currentFilterBlob = currentFilterParam ? normalizeFilterBlob(currentFilterParam) : null
  const urlFilters =
    getKnownUrlFilters(currentFilterParam) ?? (currentFilterBlob === resolvedFilterBlob ? parsedUrlFilters : {})

  // Tracks the hook's lifetime rather than a single effect run: a pending parse must survive
  // effect re-runs (any query param can change while it is in flight) and is only irrelevant
  // once the hook itself unmounts.
  const isUnmounted = useRef(false)
  useEffect(() => {
    isUnmounted.current = false
    return () => {
      isUnmounted.current = true
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <We want the useEffect to run only when location.search changes>
  useEffect(() => {
    const newCompressedFilters = getCompressedFilterParams()
    // only update if changed. If compressed filters are the same, no need to decompress them.

    //if we have resetted the filter it will be null, and we still need to run the rest of the code when we have resetted the filter
    if (newCompressedFilters == compressedFilters) {
      //As we do not update urlFilters when the urlFilters are the same, any changes to
      //Used when grouping changes, but the urlFilters are still the same
      return
    }
    setCompressedFilters(newCompressedFilters)

    // Already parsed by another consumer (or no param at all) — apply it up front so the
    // filters are never reported as empty while a filter is in fact applied. The
    // decompression below still runs, since it also applies the grouping param.
    const knownUrlFilters = getKnownUrlFilters(newCompressedFilters)
    if (knownUrlFilters) {
      publishUrlFilters(newCompressedFilters, knownUrlFilters)
    }

    getDecompressedFilterParams()
      .then(({ filters }) => {
        const parsed = parseFilterFromUrlString(filters)
        if (newCompressedFilters) {
          cacheParsedUrlFilters(newCompressedFilters, parsed)
        }
        // Apply whenever the result still describes the URL we are on. Keying this on the
        // param rather than on the effect run matters: changing any other query param
        // re-runs the effect, which then early-returns on the unchanged filter param, so
        // discarding the in-flight parse here would leave the filters empty for good.
        if (isUnmounted.current || getCompressedFilterParams() !== newCompressedFilters) {
          return
        }
        publishUrlFilters(newCompressedFilters, parsed)
      })
      .catch((error) => {
        // Clear before reporting, and under the same guard as the success path: an
        // undecodable param must not leave the previous URL's filters applied. The old
        // implementation resolved to '' on failure, so the success path cleared them; now
        // that the failure propagates, this path owns that.
        if (!isUnmounted.current && getCompressedFilterParams() === newCompressedFilters) {
          publishUrlFilters(newCompressedFilters, {})
        }

        // Failing silently here is indistinguishable from "nothing is filtered", which
        // misreports an applied filter as absent — report it instead of swallowing it.
        sentry.captureException(error as Error, {
          level: 'warning',
          tags: {
            sentry_scope: 'filter_hooks',
            issue_type: 'decompress_url_filters_failure',
          },
          extra: { compressedFilters: newCompressedFilters },
          fingerprint: ['filter_hooks', 'decompress_url_filters_failure'],
        })
      })
  }, [location.search]) // keep location.search dependency

  // Group routeTags by top level tag e.g subgrid/ (memoized)
  const tagsByTopLevelTag: Record<string, string[]> = useMemo(() => {
    const byTopLevelTag = getTagsByTopLevelTag(routeTags)
    return byTopLevelTag
  }, [routeTags])

  // Build checkedFiltersPerDropdown memoized from urlFilters + tagsByTopLevelTag
  // biome-ignore lint/correctness/useExhaustiveDependencies: <we stringify the dependency to avoid re-renders when only the reference has changed>
  const checkedFiltersPerDropdown = useMemo(() => {
    const checkedFilterPerDropdown = getCheckedFiltersPerDropdown(tagsByTopLevelTag, urlFilters)
    return checkedFilterPerDropdown
  }, [JSON.stringify(tagsByTopLevelTag), JSON.stringify(urlFilters)])

  // Compute activeTags as a stable Set (memoized)
  // biome-ignore lint/correctness/useExhaustiveDependencies: <we stringify the dependency to avoid re-renders when only the reference has changed>
  const activeTagsSet = useMemo(() => {
    const activeTags = getActiveTags(tagsByTopLevelTag, urlFilters)
    return activeTags
  }, [JSON.stringify(tagsByTopLevelTag), JSON.stringify(urlFilters)])

  //Checks if there is a filter selected
  const isActiveTagsFiltered = useMemo(() => {
    return !(activeTagsSet.size === routeTags.length && routeTags.every((tag) => activeTagsSet.has(tag)))
  }, [activeTagsSet, routeTags])

  // Return stable references
  // biome-ignore lint/correctness/useExhaustiveDependencies: <we stringify the dependency to avoid re-renders when only the reference has changed>
  return useMemo(
    () => ({
      activeTags: activeTagsSet,
      urlFilters,
      checkedFiltersPerDropdown,
      isActiveTagsFiltered,
    }),
    [activeTagsSet, JSON.stringify(urlFilters), JSON.stringify(checkedFiltersPerDropdown), isActiveTagsFiltered]
  )
}

export function useSetActiveFiltersInUrl(): (topLevelTag: string, activeFilters: string[]) => void {
  const navigate = useNavigate()
  const location = useLocation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <as we only want to rerender this on location change>
  const compressFiltersAndUpdateUrl = useCallback(
    async (filterUrlString: string) => {
      const urlParams = new URLSearchParams(location.search)
      const parsedFilters = parseFilterFromUrlString(filterUrlString)
      let compressedFilters: string

      if (filterUrlString === '') {
        compressedFilters = ''
        urlParams.delete(FILTER_QUERYSTRING_PARAM)
      } else {
        compressedFilters = await compressFilters(filterUrlString)
        cacheParsedUrlFilters(compressedFilters, parsedFilters)
        urlParams.set(FILTER_QUERYSTRING_PARAM, compressedFilters)
      }

      const newUrl = `${location.pathname}?${urlParams.toString()}${location.hash}`
      navigate(newUrl, { replace: true })
      flushSync(() => {
        publishUrlFilters(compressedFilters || null, parsedFilters)
      })
    },
    [location]
  )

  const mergeNewFiltersWithExisting = useCallback(
    (topLevelTag: string, newFilters: string[], existingFilters: Record<string, string>) => {
      if (newFilters.length === 0) {
        delete existingFilters[topLevelTag]
      } else {
        existingFilters[topLevelTag] = newFilters
          .map((f) => f.split('/')[1])
          .map((v) => encodeURIComponent(v))
          .join(',')
      }
    },
    []
  )

  const setActiveFiltersInUrl = useCallback(
    async (topLevelTag: string, activeFilters: string[]) => {
      // An undecodable existing param must not stop the user replacing it. The read
      // effect has already reported that blob to Sentry, so treat it as no filter here
      // rather than reporting the same failure twice or rejecting out of a click handler.
      const currentFiltersString = await getDecompressedFilterParams()
        .then(({ filters }) => filters)
        .catch(() => '')
      const urlFiltersByTopLevelTag = parseExistingFilters(currentFiltersString)

      mergeNewFiltersWithExisting(topLevelTag, activeFilters, urlFiltersByTopLevelTag)

      const newFilterUrlString = getFilterString(urlFiltersByTopLevelTag)

      if (newFilterUrlString !== undefined) {
        compressFiltersAndUpdateUrl(newFilterUrlString)
      } else {
        compressFiltersAndUpdateUrl('')
      }
    },
    [compressFiltersAndUpdateUrl, mergeNewFiltersWithExisting]
  )

  return setActiveFiltersInUrl
}
